import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControlLabel, FormGroup,
  IconButton, TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import adminApi from '../../../utils/admin.api';

const ACCESS_COLOR = {
  approved: 'success',
  revoked: 'error',
  pending: 'warning',
};

const EditAccessDialog = ({ open, onClose, requestSummary, onDone }) => {
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // checkedIds = demos the admin wants to have access (approved)
  const [checkedIds, setCheckedIds] = useState([]);
  const [notes, setNotes] = useState('');

  // ── Load detail on open ───────────────────────────────────────────────────────

  useEffect(() => {
    if (open && requestSummary) {
      setDetailLoading(true);
      setApiError('');
      setNotes('');

      adminApi
        .getRequestDetail(requestSummary.request_id)
        .then(({ data }) => {
          const d = data?.data ?? null;
          setDetail(d);
          // Pre-check only currently approved demos
          const approvedIds = (d?.requested_demos ?? [])
            .filter((demo) => demo.access_status === 'approved')
            .map((demo) => demo.id);
          setCheckedIds(approvedIds);
        })
        .catch(() => setApiError('Failed to load request details.'))
        .finally(() => setDetailLoading(false));
    } else {
      setDetail(null);
      setCheckedIds([]);
    }
  }, [open, requestSummary]);

  // ── Handlers ──────────────────────────────────────────────────────────────────

  const toggleDemo = (id) => {
    setCheckedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setApiError('');
    try {
      const allIds = (detail?.requested_demos ?? []).map((d) => d.id);
      const revokedIds = allIds.filter((id) => !checkedIds.includes(id));

      await adminApi.modifyRequestAccess(requestSummary.request_id, {
        approved_demo_ids: checkedIds,
        revoked_demo_ids: revokedIds,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });

      onDone();
      onClose();
    } catch (err) {
      setApiError(err?.response?.data?.detail || 'Failed to modify access.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────────────

  const demos = detail?.requested_demos ?? [];
  const hasChanges = demos.some((demo) => {
    const wasApproved = demo.access_status === 'approved';
    const isChecked = checkedIds.includes(demo.id);
    return wasApproved !== isChecked;
  });

  if (!requestSummary) return null;

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
        Modify Access — {requestSummary?.user_name || requestSummary?.user_email || ''}
        <IconButton size="small" onClick={onClose} disabled={submitting}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
        <Box>
          <Typography variant="subtitle2">User</Typography>
          <Typography>
            {requestSummary.user_name || '—'} ({requestSummary.user_email || '—'})
          </Typography>
        </Box>

        <Divider />

        {detailLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Demo Access — check to grant, uncheck to revoke
            </Typography>
            <FormGroup>
              {demos.map((demo) => (
                <Box
                  key={demo.id}
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={checkedIds.includes(demo.id)}
                        onChange={() => toggleDemo(demo.id)}
                        disabled={submitting}
                      />
                    }
                    label={`${demo.title} (${demo.app})`}
                  />
                  <Chip
                    label={demo.access_status}
                    color={ACCESS_COLOR[demo.access_status] ?? 'default'}
                    size="small"
                    variant="outlined"
                    sx={{ textTransform: 'capitalize', mr: 1 }}
                  />
                </Box>
              ))}
            </FormGroup>
          </Box>
        )}

        <TextField
          label="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          fullWidth
          multiline
          rows={2}
          disabled={submitting}
        />

        {apiError && <Typography color="error">{apiError}</Typography>}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!hasChanges || submitting || detailLoading}
          onClick={handleSubmit}
        >
          {submitting ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditAccessDialog;
